// src/components/shell/AppShell.tsx
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";

// src/components/ui/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/shell/AppShell.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var LG_BREAKPOINT_QUERY = "(min-width: 1024px)";
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === "undefined" ? true : window.matchMedia(LG_BREAKPOINT_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(LG_BREAKPOINT_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}
function AppShell({
  sidebar,
  topbar,
  children,
  productSlug,
  sidebarWidth = "16rem",
  sidebarLabel = "Main navigation"
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const closeButtonRef = useRef(null);
  const menuButtonRef = useRef(null);
  const isDesktop = useIsDesktop();
  const closeSidebar = () => {
    setSidebarOpen(false);
    if (!isDesktop) {
      requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  };
  useEffect(() => {
    if (!sidebarOpen || isDesktop) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeSidebar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen, isDesktop]);
  useEffect(() => {
    if (sidebarOpen && !isDesktop) {
      closeButtonRef.current?.focus();
    }
  }, [sidebarOpen, isDesktop]);
  const sidebarInert = !isDesktop && !sidebarOpen;
  const restInert = !isDesktop && sidebarOpen;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "flex min-h-screen flex-col bg-background text-foreground",
      "data-product": productSlug,
      children: [
        /* @__PURE__ */ jsx2(
          "header",
          {
            className: "sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-sm",
            inert: restInert ? "" : void 0,
            children: /* @__PURE__ */ jsxs("div", { className: "flex h-14 items-center gap-3 px-4 sm:px-6", children: [
              /* @__PURE__ */ jsx2(
                Button,
                {
                  ref: menuButtonRef,
                  variant: "ghost",
                  size: "icon",
                  className: "lg:hidden",
                  "aria-label": sidebarOpen ? "Close navigation" : "Open navigation",
                  "aria-expanded": sidebarOpen,
                  "aria-controls": "appshell-sidebar",
                  onClick: () => sidebarOpen ? closeSidebar() : setSidebarOpen(true),
                  children: sidebarOpen ? /* @__PURE__ */ jsx2(X, {}) : /* @__PURE__ */ jsx2(Menu, {})
                }
              ),
              /* @__PURE__ */ jsx2("div", { className: "flex-1", children: topbar })
            ] })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-1", children: [
          /* @__PURE__ */ jsxs(
            "aside",
            {
              ref: sidebarRef,
              id: "appshell-sidebar",
              "aria-label": sidebarLabel,
              role: !isDesktop && sidebarOpen ? "dialog" : void 0,
              "aria-modal": !isDesktop && sidebarOpen ? true : void 0,
              inert: sidebarInert ? "" : void 0,
              className: cn(
                "fixed inset-y-14 left-0 z-20 border-r border-border bg-card transition-transform duration-200 ease-out lg:static lg:inset-y-0 lg:translate-x-0",
                sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
              ),
              style: { width: sidebarWidth },
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between border-b border-border p-3 lg:hidden", children: [
                  /* @__PURE__ */ jsx2("h2", { className: "text-sm font-semibold", children: sidebarLabel }),
                  /* @__PURE__ */ jsx2(
                    Button,
                    {
                      ref: closeButtonRef,
                      variant: "ghost",
                      size: "icon",
                      "aria-label": "Close navigation",
                      onClick: closeSidebar,
                      children: /* @__PURE__ */ jsx2(X, {})
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx2("nav", { className: "h-full overflow-y-auto p-4", children: sidebar })
              ]
            }
          ),
          sidebarOpen && !isDesktop && /* @__PURE__ */ jsx2(
            "div",
            {
              className: "fixed inset-0 z-10 bg-black/30 lg:hidden",
              onClick: closeSidebar,
              "aria-hidden": true
            }
          ),
          /* @__PURE__ */ jsx2(
            "main",
            {
              className: "flex-1 p-4 sm:p-6 lg:p-8",
              inert: restInert ? "" : void 0,
              children
            }
          )
        ] })
      ]
    }
  );
}
export {
  AppShell
};
//# sourceMappingURL=AppShell.js.map